/**
 * Prompt to add the app to the home screen.
 *
 * Two very different platforms behind one card:
 *  - Android/Chrome gives us a real install prompt we can trigger.
 *  - iOS has no such API, so all we can do is show where the Share button
 *    is and what to tap. That is also the platform where it matters most:
 *    Safari wipes a site's storage after 7 days of no visits, and only an
 *    installed app is exempt.
 */
import { useState, type ReactNode } from 'react';
import { useT } from '../i18n/i18n';
import { useInstall } from '../lib/install';
import { Icons } from './ui';

/** The iOS share glyph, so the instruction points at something recognisable. */
function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ?? 'h-4 w-4'}
      aria-hidden="true"
    >
      <path d="M12 15V3.5M8.5 7 12 3.5 15.5 7" />
      <path d="M6 12.5v6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}

/** Safari's options button, drawn as the ellipsis-in-a-rounded-box it is. */
function OptionsIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className={className ?? 'h-4 w-4'}
      aria-hidden="true"
    >
      <rect x="3" y="6" width="18" height="12" rx="3" />
      <circle cx="8.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary">
        {n}
      </span>
      <span className="flex flex-wrap items-center gap-1">{children}</span>
    </li>
  );
}

export default function InstallCard() {
  const t = useT();
  const { shouldOffer, platform, canPromptNatively, promptInstall, dismiss } = useInstall();
  const [busy, setBusy] = useState(false);

  if (!shouldOffer) return null;

  const onInstall = async () => {
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card animate-float-up relative overflow-hidden p-4">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t.common.actions.close}
        className="absolute right-2 top-2 rounded-full p-2 text-ink-faint hover:bg-surface-2"
      >
        {Icons.x('h-4 w-4')}
      </button>

      <div className="flex items-start gap-3 pr-8">
        <span className="mt-0.5 shrink-0 rounded-xl bg-primary-soft p-2 text-primary">
          {Icons.home('h-5 w-5')}
        </span>
        <div className="min-w-0">
          <h2 className="text-base">{t.install.title}</h2>
          <p className="mt-1 text-sm text-ink-soft">
            {platform === 'ios' ? t.install.bodyIos : t.install.body}
          </p>
        </div>
      </div>

      {canPromptNatively ? (
        <button
          type="button"
          onClick={() => void onInstall()}
          disabled={busy}
          className="btn-primary mt-3 min-h-[44px] w-full px-4 disabled:opacity-60"
        >
          {busy ? t.install.installing : t.install.addButton}
        </button>
      ) : (
        <ol className="mt-3 space-y-2 text-sm text-ink-soft">
          <Step n={1}>
            {t.install.step1Before}
            <OptionsIcon className="h-4 w-4 text-accent" />
            <span className="font-bold text-ink">{t.install.optionsLabel}</span>
            {t.install.step1After}
          </Step>
          <Step n={2}>
            {t.install.step2Before}
            <ShareIcon className="h-4 w-4 text-accent" />
            <span className="font-bold text-ink">{t.install.shareLabel}</span>
          </Step>
          <Step n={3}>
            {t.install.step3Before}
            <span className="font-bold text-ink">{t.install.addToHomeScreen}</span>
          </Step>
        </ol>
      )}

      <p className="mt-3 border-t border-line pt-3 text-xs leading-relaxed text-ink-faint">
        {t.install.finePrint}
      </p>
    </section>
  );
}
